// SSO sign-in hook for the task pane.
//
// Opens the Office dialog (dialog.html) for the chosen provider, receives the
// id_token back via DialogMessageReceived, exchanges it with the backend
// (entra_id_login / auth0_login) and then establishes the react-auth-kit
// session — the same session the username/password flow (UseAuthsignIn.tsx)
// creates, so the rest of the add-in is unaffected.

import { useState } from "react";
import { useSignIn } from "react-auth-kit";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import posthog from "posthog-js";
import { apiURL } from "../../helper/urls";
import { SsoProvider } from "../../helper/authConfig";

interface SsoMessage {
  status: "success" | "error";
  provider?: SsoProvider;
  idToken?: string;
  user?: Record<string, unknown>;
  error?: string;
}

// Office measures dialog size as a percentage of the screen.
const DIALOG_WIDTH_PERCENT = 40;
const DIALOG_HEIGHT_PERCENT = 60;

// Mirrors the local session length of the username/password flow
// (UseAuthsignIn.tsx). Note react-auth-kit measures expiresIn in minutes; the
// actual token lifetime is still governed by the IdP token the backend returns.
const SESSION_EXPIRES_IN_MINUTES = 3600 * 48;

const useSsoSignIn = () => {
  const signIn = useSignIn();
  const navigate = useNavigate();
  const [activeProvider, setActiveProvider] = useState<SsoProvider | null>(null);
  const [error, setError] = useState<string | null>(null);

  const completeSignIn = async (provider: SsoProvider, message: SsoMessage): Promise<void> => {
    try {
      const endpoint = provider === "auth0" ? "auth0_login" : "entra_id_login";
      const body = { user: message.user, tokens: { id_token: message.idToken } };

      const res = await axios.post(apiURL(endpoint), body, {
        headers: { "Content-Type": "application/json" },
      });

      const email = res.data?.email || (message.user?.email as string) || (message.user?.upn as string) || "";
      // Only trust a session the backend actually blessed — never fall back to
      // the raw IdP token if the exchange didn't return an access_token.
      const token = res.data?.access_token;
      if (!token) {
        setActiveProvider(null);
        setError("Sign-in failed. Please try again.");
        return;
      }

      const ok = signIn({
        token,
        expiresIn: SESSION_EXPIRES_IN_MINUTES,
        tokenType: "Bearer",
        authState: {
          email,
          authType: provider === "auth0" ? "Auth0" : "EntraId",
        },
      });

      if (ok) {
        if (email) {
          posthog.identify(email, { email, $initial_referrer: document.referrer });
          posthog.capture("user_signed_in", { method: provider });
        }
        setActiveProvider(null);
        navigate("/");
      } else {
        setActiveProvider(null);
        setError("Log in unsuccessful. Please try again.");
      }
    } catch (err) {
      setActiveProvider(null);
      const detail =
        axios.isAxiosError(err) && err.response?.data?.detail
          ? err.response.data.detail
          : "Sign-in failed. Please try again.";
      setError(detail);
      posthog.capture("sso_sign_in_error", { provider, error: detail });
    }
  };

  const startSso = (provider: SsoProvider): void => {
    if (typeof Office === "undefined" || !Office?.context?.ui?.displayDialogAsync) {
      setError("Single sign-on is only available inside Microsoft Word.");
      return;
    }

    setError(null);
    setActiveProvider(provider);
    posthog.capture("sso_sign_in_attempt", { provider });

    const url = `${window.location.origin}/dialog.html?provider=${provider}`;

    Office.context.ui.displayDialogAsync(
      url,
      { height: DIALOG_HEIGHT_PERCENT, width: DIALOG_WIDTH_PERCENT, promptBeforeOpen: false },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          setActiveProvider(null);
          setError("Could not open the sign-in window. Please try again.");
          return;
        }

        const dialog = result.value;

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
          let message: SsoMessage;
          try {
            message = JSON.parse((arg as { message: string }).message);
          } catch {
            dialog.close();
            setActiveProvider(null);
            setError("Unexpected response from the sign-in window.");
            return;
          }

          if (message.status !== "success" || !message.idToken) {
            dialog.close();
            setActiveProvider(null);
            setError(message.error || "Sign-in was cancelled.");
            posthog.capture("sso_sign_in_error", { provider, error: message.error });
            return;
          }

          dialog.close();
          void completeSignIn(provider, message);
        });

        // Fires when the user closes the dialog manually (e.g. code 12006).
        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
          setActiveProvider((current) => (current === provider ? null : current));
        });
      }
    );
  };

  return { startSso, activeProvider, error, setError };
};

export default useSsoSignIn;
